import snakeCaseKeys from 'snakecase-keys';
import queryString from 'query-string';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import fromPairs from 'lodash/fromPairs';
import { replaceSpace } from 'shared/modules';
import { getResultFromStoreByKey } from 'shared/helpers/common';
import TITLE from 'shared/constants/seoTitles';
import DESCRIPTION from 'shared/constants/seoDescriptions';
import META_HEADER from 'shared/constants/seoHeaders';
import types from 'shared/constants/json/transportTypes.json';
import CITIES from 'shared/constants/json/cities.json';
import COUNTRIES from 'shared/constants/json/countries.json';
import REGIONS from 'shared/constants/json/regions.json';
import RUBRICS from 'shared/constants/json/rubrics.json';
import { setLocation, setSeoSearchUrl, seoRequest } from 'shared/actions/globals';
import {
  setFormVocabulary,
  selectedActions,
  sparesFormFillThunks,
  resultsThunks,
  sparesSetThunks,
  resetAll,
} from 'shared/actions/searchForm';
import API from 'shared/constants/api';
import { compareByTitle, getSeoParamsTitles } from 'shared/helpers/arrays';
import { translateSeoLinks, generateSparesSearchUrl } from 'shared/helpers/urls';
import { getAdsContext, getPriceValuation } from '../../helpers/adsAndMetrics';
import { removeLocaleFromHref } from '../../helpers/locale';

const getCanonicalLinks = (fetch, q, isAuto) =>
  (!q.make || (q.make && !q.model)) && isAuto
    ? fetch(`${API.SPARE_PARTS.MAKES_COUNT}?${queryString.stringify(snakeCaseKeys(q))}`).then(r =>
        r.json(),
      )
    : null;

export default async function({ store, query, baseUrl, path, fetch, translate }) {
  const missingVocabularyFields = [];
  const url = removeLocaleFromHref(`${baseUrl}${path}`.replace('//', '/'));

  // Skip this route for permalinks
  if (url.includes('.html')) {
    return null;
  }

  const currentPage = parseFloat(query.page || 1);
  const state = store.getState();
  const { globals: { location: storeLocation }, searchForm: { vocabulary } } = state;
  const seoResponse = await store.dispatch(seoRequest(url));
  if (isEmpty(seoResponse)) {
    return null;
  }
  store.dispatch(setSeoSearchUrl({ ...seoResponse, url }));
  const { characteristics, kind, meta, ...seoQuery } = seoResponse;

  if (seoQuery.location && seoQuery.location.is_country) {
    return null;
  }
  if (storeLocation && storeLocation.is_country) {
    store.dispatch(setLocation(null));
  }

  const transportType = seoQuery.transport || types.find(i => i.slug === 'avto');
  const transportID = transportType.id;
  if (!vocabulary.categories) {
    missingVocabularyFields.push('categories');
  }
  const fetchedVocabulary = await Promise.all(
    missingVocabularyFields.map(
      field =>
        API.VOCABULARY[field]
          ? fetch(API.VOCABULARY[field](transportType.id))
              .then(r => r.json())
              .then(response => ({ [field]: response }))
          : Promise.resolve({}),
    ),
  );
  const mergedVocabulary = fetchedVocabulary.reduce(
    (results, pair) => ({ ...results, ...pair }),
    {},
  );
  store.dispatch(
    setFormVocabulary({
      ...mergedVocabulary,
    }),
  );
  const { searchForm: { vocabulary: { categories } } } = store.getState();
  let body;
  for (let a = 0; a < categories.length; a += 1) {
    const buf = categories[a] && categories[a].title && categories[a].id;
    if (buf && query.category && String(buf) === query.category) {
      body = categories[a];
      break;
    }
  }
  const prevSelectedTransport = state.searchForm.selected && state.searchForm.selected.transport;
  if (prevSelectedTransport && prevSelectedTransport.id !== transportID) {
    store.dispatch(resetAll());
  }

  const storeParams = fromPairs(
    Object.entries(store.getState().searchForm.selected)
      .map(([key, value]) => [key, value && value.id ? value.id : value])
      .filter(([, value]) => value && (typeof value === 'string' || typeof value === 'number')),
  );

  const selected = store.getState().searchForm.selected || {};
  const queryRubric = query.rubric || (selected.rubric && selected.rubric.id);
  const querySparetype = query.sparetype || (selected.sparetype && selected.sparetype.id);
  const currency = selected && selected.currency;
  const q = {
    ...storeParams,
    ...query,
    transport: transportID,
    ...(queryRubric ? { rubric: queryRubric } : {}),
    ...(querySparetype ? { sparetype: querySparetype } : {}),
  };
  let location = store.getState().globals.location;

  if (seoQuery.make) {
    q.make = seoQuery.make.id;
  }
  if (seoQuery.model) {
    q.model = seoQuery.model.id;
  }
  if (seoQuery.rubric) {
    q.rubric = seoQuery.rubric.id;
  }
  if (seoQuery.part_type) {
    q.sparetype = seoQuery.part_type.id;
  }

  if (seoQuery.location) {
    location = seoQuery.location;
    store.dispatch(setLocation(seoQuery.location));
  }

  q.location = (location && location.id) || undefined;
  q.currency = currency.slug;

  store.dispatch(sparesSetThunks.count(pick(q, Object.keys(state.searchForm.selected))));
  store.dispatch(selectedActions.set.transport(transportType));

  const isAuto = transportType.slug === 'avto';

  const [makes, rubrics, canonicalList] = await Promise.all([
    fetch(API.VOCABULARY.makes(transportID)).then(r => r.json()),
    fetch(API.VOCABULARY.rubrics(transportID)).then(r => r.json()),
    getCanonicalLinks(fetch, q, isAuto),
  ]);

  store.dispatch(
    setFormVocabulary({
      makes: [...makes].sort(compareByTitle),
      rubrics,
    }),
  );

  const rubric = q.rubric
    ? rubrics.find(rubricItem => rubricItem.id === parseFloat(q.rubric))
    : null;
  const make =
    seoQuery.make || (q.make ? makes.find(makeItem => makeItem.id === parseFloat(q.make)) : null);

  const seoLocation = [...CITIES, ...REGIONS, ...COUNTRIES].find(
    item => item.id === (location && location.id),
  );
  const isRegion = location && location.is_region;
  const seoDataTitles = getSeoParamsTitles(
    { ...seoQuery, location: seoLocation, rubric },
    translate,
  );
  const rubricSlug = rubric && rubric.slug;

  const title = replaceSpace(TITLE.SPARE_PARTS(seoDataTitles, rubricSlug, translate));
  const description = replaceSpace(
    DESCRIPTION.SPARE_PARTS(seoDataTitles, rubricSlug, translate, isRegion),
  );
  const metaHeader = replaceSpace(
    META_HEADER.SPARE_PARTS(seoDataTitles, rubricSlug, translate, isRegion),
  );

  const queryParams = queryString.stringify(snakeCaseKeys(q));
  await store.dispatch(resultsThunks.searchSparesResults(queryParams));

  const result = getResultFromStoreByKey(store, `spares-${queryParams}`);

  // make must be earlier than model
  if (q.make) {
    await store.dispatch(sparesFormFillThunks.make(q.make));
  }
  // rubric must be earlier than sparetype
  if (q.rubric) {
    await store.dispatch(sparesFormFillThunks.rubric(q.rubric));
  }
  Object.keys(q).filter(key => key !== 'make' && key !== 'rubric').forEach(key => {
    const item = q[key];
    if (sparesFormFillThunks[key]) {
      store.dispatch(sparesFormFillThunks[key](item));
    }
  });

  const seo = {
    ...seoQuery,
    ...location,
  };
  const targetingArguments = getAdsContext({
    make: seo.make,
    model: seo.model,
    isMain: seo.isMain,
    isNew: seo.isNew,
    rubric: seo.rubric,
    isCustomCleared: seo.isCustomCleared,
    location: seo.location,
    isCountry: seo.isCountry,
    transport: transportType,
    category: body,
    engine: seo.engine,
    dealer: seo.rubric,
    price: getPriceValuation({ from: q.priceFrom, to: q.priceTo }, q.currency),
    page: baseUrl.replace(/\//g, ''),
    id: seo.slug,
  });

  const linkPrefix = generateSparesSearchUrl({
    transport: transportType,
    make,
    rubric: seoQuery.rubric,
    model: seoQuery.model,
    sparetype: seoQuery.part_type,
  });

  //
  const isLastPage = !(result && result.next);
  const rubricsData = translateSeoLinks({ rubrics: RUBRICS }, translate);
  return {
    url,
    transportType,
    currentPage,
    queryParams,
    title,
    description,
    metaHeader,
    seoQuery,
    isLastPage,
    targetingArguments,
    linkPrefix,
    q,
    characteristics,
    canonicalList,
    state,
    seoResponse,
    rubricsLinks: rubricsData.rubrics,
  };
}



// WEBPACK FOOTER //
// ./src/shared/routes/spareparts/index.js